/**
 * Schema Stage — Stage 1: Structural validation.
 *
 * ARCHITECTURE v4.0 — Phase 2, Step 2.1
 *
 * Validates:
 * - Required fields present (id, familyId, module, question, questionType)
 * - familyId exists in taxonomy
 * - module matches taxonomy
 * - tier/levelCode/group/skill/subSkill match taxonomy
 * - feedback.explanationText present
 * - options/correctAnswer valid for MC
 */

import type { ValidationIssue } from '../issueContract';
import { blocker, coach, info } from '../issueContract';
import { getPolicy } from '../policyEngine';
import { FAMILY_BY_ID, type Question } from '../../../content';
import { SEVERITY } from '../severityScale';
import { STAGES } from '../stageDefinitions';
import { getQuestionDifficulty, isDifficultyConsistentWithTier, expectedDifficultyBandForTier } from '../../../shared/proxyContracts/difficulty';

// SSOT for stage numbers — see stageDefinitions.ts (G-16 fix, RC-3)
const STAGE = STAGES.SCHEMA;

/**
 * Run schema validation on a question.
 * Returns ValidationIssue[] (blocker/coach/info).
 */
export function runSchemaStage(question: Question): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const qId = question.id ?? 'UNKNOWN';
  const fId = question.familyId ?? 'UNKNOWN';

  // ── Required fields ──────────────────────────────────────────────────────

  if (!question.id || typeof question.id !== 'string' || question.id.trim() === '') {
    issues.push(blocker('schema', STAGE, 'SCHEMA_MISSING_ID', 'Missing or empty question id', {
      field: 'id',
      questionId: qId,
      familyId: fId
    }));
  }
  if (!question.familyId || typeof question.familyId !== 'string' || question.familyId.trim() === '') {
    issues.push(blocker('schema', STAGE, 'SCHEMA_MISSING_FAMILY_ID', 'Missing or empty familyId', {
      field: 'familyId',
      questionId: qId,
      familyId: fId
    }));
  }
  if (!question.module || typeof question.module !== 'string') {
    issues.push(blocker('schema', STAGE, 'SCHEMA_MISSING_MODULE', 'Missing module field', {
      field: 'module',
      questionId: qId,
      familyId: fId
    }));
  }
  if (!question.question || typeof question.question !== 'string' || question.question.trim() === '') {
    issues.push(blocker('schema', STAGE, 'SCHEMA_MISSING_QUESTION_TEXT', 'Missing or empty question text', {
      field: 'question',
      questionId: qId,
      familyId: fId
    }));
  }
  if (!question.questionType || typeof question.questionType !== 'string') {
    issues.push(blocker('schema', STAGE, 'SCHEMA_MISSING_QUESTION_TYPE', 'Missing questionType field', {
      field: 'questionType',
      questionId: qId,
      familyId: fId
    }));
  }

  // ── Taxonomy alignment ───────────────────────────────────────────────────

  if (question.familyId) {
    const familyDef = FAMILY_BY_ID.get(question.familyId);
    if (!familyDef) {
      issues.push(blocker('schema', STAGE, 'SCHEMA_UNKNOWN_FAMILY', `familyId "${question.familyId}" not found in taxonomy`, {
        field: 'familyId',
        actual: question.familyId,
        questionId: qId,
        familyId: fId
      }));
    } else {
      // Module match
      if (question.module && question.module !== familyDef.module) {
        issues.push(blocker('schema', STAGE, 'SCHEMA_MODULE_MISMATCH', `module "${question.module}" does not match taxonomy "${familyDef.module}" for family "${question.familyId}"`, {
          field: 'module',
          expected: familyDef.module,
          actual: question.module,
          questionId: qId,
          familyId: fId
        }));
      }

      // Tier match
      if (question.tier !== undefined && question.tier !== familyDef.tier) {
        issues.push(blocker('schema', STAGE, 'SCHEMA_TIER_MISMATCH', `tier ${question.tier} does not match taxonomy tier ${familyDef.tier} for family "${question.familyId}"`, {
          field: 'tier',
          expected: String(familyDef.tier),
          actual: String(question.tier),
          questionId: qId,
          familyId: fId
        }));
      }

      // Skill match
      if (question.skill && question.skill !== familyDef.skill) {
        issues.push(coach('schema', STAGE, 'SCHEMA_SKILL_MISMATCH', `skill "${question.skill}" does not match taxonomy "${familyDef.skill}"`, {
          field: 'skill',
          expected: familyDef.skill,
          actual: question.skill,
          questionId: qId,
          familyId: fId,
          severity: SEVERITY.REVIEW
        }));
      }

      // SubSkill match
      if (question.subSkill && question.subSkill !== familyDef.subSkill) {
        issues.push(coach('schema', STAGE, 'SCHEMA_SUBSKILL_MISMATCH', `subSkill "${question.subSkill}" does not match taxonomy "${familyDef.subSkill}"`, {
          field: 'subSkill',
          expected: familyDef.subSkill,
          actual: question.subSkill,
          questionId: qId,
          familyId: fId,
          severity: SEVERITY.MEDIUM
        }));
      }
    }
  }

  // ── MC-specific validation ───────────────────────────────────────────────

  if (question.questionType === 'multiple_choice') {
    // Bypass: two_step_judge MC stores its real options inside
    // data.step1.options / data.step2.options. The top-level options[]
    // is intentionally empty. Skip MC option/correctAnswer validation.
    const mcData = (question as Record<string, unknown>).data as Record<string, unknown> | undefined;
    const isTwoStepJudge = mcData?.mode === 'two_step_judge';
    if (isTwoStepJudge) {

      // Skip — handled by interactive validators.
    } else if (!question.options || !Array.isArray(question.options) || question.options.length < 2) {
      issues.push(blocker('schema', STAGE, 'SCHEMA_MC_MISSING_OPTIONS', 'Multiple choice question must have at least 2 options', {
        field: 'options',
        questionId: qId,
        familyId: fId
      }));
    } else {
      // Check for empty options
      const emptyIdx = question.options.findIndex(o => typeof o !== 'string' || o.trim() === '');
      if (emptyIdx >= 0) {
        issues.push(blocker('schema', STAGE, 'SCHEMA_MC_EMPTY_OPTION', `Option at index ${emptyIdx} is empty`, {
          field: `options[${emptyIdx}]`,
          questionId: qId,
          familyId: fId
        }));
      }

      // Check for duplicate options
      const normalized = question.options.map(o => (o ?? '').trim().toLowerCase());
      const uniqueSet = new Set(normalized);
      if (uniqueSet.size < normalized.length) {
        issues.push(coach('schema', STAGE, 'SCHEMA_MC_DUPLICATE_OPTIONS', 'Duplicate options detected', {
          field: 'options',
          questionId: qId,
          familyId: fId,
          severity: SEVERITY.REVIEW
        }));
      }

      // ── MC option count range (D2: minimaal 3, maximaal 5) ─────────────────
      // Tightened from ≥2 to 3-5 per SEED_REVIEW_CHECKLIST §D2
      if (question.options && Array.isArray(question.options) && question.options.length >= 2) {
        if (question.options.length < 3) {
          issues.push(coach('schema', STAGE, 'SCHEMA_MC_TOO_FEW_OPTIONS', `Multiple choice has only ${question.options.length} options — minimum 3 recommended for plausible distractors`, {
            field: 'options',
            expected: '3-5',
            actual: String(question.options.length),
            questionId: qId,
            familyId: fId,
            severity: SEVERITY.NEAR_MEDIUM
          }));
        }
        if (question.options.length > 5) {
          issues.push(coach('schema', STAGE, 'SCHEMA_MC_TOO_MANY_OPTIONS', `Multiple choice has ${question.options.length} options — maximum 5 recommended to avoid cognitive overload`, {
            field: 'options',
            expected: '3-5',
            actual: String(question.options.length),
            questionId: qId,
            familyId: fId,
            severity: SEVERITY.LOW
          }));
        }
      }
    }
    if (isTwoStepJudge) {

      // Two-step judge MC: correctIndex lives inside data.step1/step2.
      // Skip top-level correctAnswer range check.
    } else if (question.correctAnswer === undefined || question.correctAnswer === null) {
      issues.push(blocker('schema', STAGE, 'SCHEMA_MC_MISSING_CORRECT_ANSWER', 'Multiple choice question must have correctAnswer', {
        field: 'correctAnswer',
        questionId: qId,
        familyId: fId
      }));
    } else if (question.options && (question.correctAnswer < 0 || question.correctAnswer >= question.options.length)) {
      issues.push(blocker('schema', STAGE, 'SCHEMA_MC_INVALID_CORRECT_ANSWER', `correctAnswer ${question.correctAnswer} out of range [0, ${question.options.length - 1}]`, {
        field: 'correctAnswer',
        expected: `0-${question.options.length - 1}`,
        actual: String(question.correctAnswer),
        questionId: qId,
        familyId: fId
      }));
    }
  }

  // ── fill_in acceptedAnswers validation (D3) ──────────────────────────────

  if (question.questionType === 'fill_in') {
    const data = (question as Record<string, unknown>).data as Record<string, unknown> | undefined;
    const acceptedAnswers = data?.acceptedAnswers as string[] | undefined;
    if (!acceptedAnswers || !Array.isArray(acceptedAnswers) || acceptedAnswers.length === 0) {
      issues.push(blocker('schema', STAGE, 'SCHEMA_FILL_IN_MISSING_ACCEPTED', 'fill_in question must have data.acceptedAnswers with at least 1 entry', {
        field: 'data.acceptedAnswers',
        questionId: qId,
        familyId: fId
      }));
    } else {
      // Check for empty entries
      const emptyIdx = acceptedAnswers.findIndex(a => typeof a !== 'string' || a.trim() === '');
      if (emptyIdx >= 0) {
        issues.push(coach('schema', STAGE, 'SCHEMA_FILL_IN_EMPTY_ANSWER', `acceptedAnswers[${emptyIdx}] is empty`, {
          field: `data.acceptedAnswers[${emptyIdx}]`,
          questionId: qId,
          familyId: fId,
          severity: SEVERITY.MEDIUM
        }));
      }
    }
  }

  // ── Feedback validation ──────────────────────────────────────────────────

  const explanation = question.feedback?.explanationText;
  if (!explanation || typeof explanation !== 'string' || explanation.trim() === '') {
    issues.push(blocker('schema', STAGE, 'SCHEMA_MISSING_EXPLANATION', 'Missing feedback.explanationText', {
      field: 'feedback.explanationText',
      questionId: qId,
      familyId: fId,
      suggestion: 'Add a clear, educational explanation of the correct answer'
    }));
  }

  // ── conceptId presence check (D1) ────────────────────────────────────────

  {
    const conceptId = (question as Record<string, unknown>).conceptId;
    if (!conceptId || typeof conceptId !== 'string' || conceptId.trim() === '') {
      issues.push(info('schema', STAGE, 'SCHEMA_MISSING_CONCEPT_ID', 'Question is missing conceptId — recommended for concept coverage tracking', {
        field: 'conceptId',
        questionId: qId,
        familyId: fId,
        severity: SEVERITY.COSMETIC,
        suggestion: 'Add a descriptive conceptId (e.g., "breuken-gelijknamig-optellen") for family-level concept coverage analysis'
      }));
    }
  }

  // ── variantIndex presence check (D1) ─────────────────────────────────────

  {
    const variantIndex = (question as Record<string, unknown>).variantIndex;
    if (variantIndex === undefined || variantIndex === null) {
      issues.push(info('schema', STAGE, 'SCHEMA_MISSING_VARIANT_INDEX', 'Question is missing variantIndex — recommended for uniqueness within family', {
        field: 'variantIndex',
        questionId: qId,
        familyId: fId,
        severity: SEVERITY.INFO,
        suggestion: 'Add a unique variantIndex (integer) within the family for deduplication'
      }));
    }
  }

  // ── ID format validation ─────────────────────────────────────────────────

  if (question.id && question.familyId) {
    if (!question.id.startsWith(question.familyId)) {
      issues.push(coach('schema', STAGE, 'SCHEMA_ID_FORMAT', `Question id "${question.id}" should start with familyId "${question.familyId}"`, {
        field: 'id',
        expected: `${question.familyId}-NNN`,
        actual: question.id,
        questionId: qId,
        familyId: fId,
        severity: SEVERITY.NEAR_LOW
      }));
    }
  }

  // ── FG1 — Difficulty ↔ Tier consistency guard ────────────────────────────
  // ARCHITECTURAL_PEER_REVIEW_PLAN_2026 §FG1.
  //
  // Today citoDifficulty is interpreted IRT-style (higher = harder) but the
  // NAME suggests a Cito p-value (higher = easier). There's no math bug yet
  // because the ELO engine doesn't read the field. The day an engineer wires
  // a 2-PL coupling, the name introduces a silent inversion. This guard
  // catches inverted seeds BEFORE production by checking the value sits in
  // its tier's expected band.
  //
  // Emitted as `coach` (severity MEDIUM) — never blocks the gate. When the
  // operator flips `schemas: { '<questionType>': 'shadow' }` it routes to
  // the shadow store via applyEnforcementModes for the FG1 impact report.
  // Anti-bandaid principle #4: observability before enforcement.
  {
    const rawDifficulty = (question as Record<string, unknown>).citoDifficulty;
    if (typeof rawDifficulty === 'number' && Number.isFinite(rawDifficulty) && typeof question.tier === 'number' && question.tier > 0) {
      // GDLA-09 adoption: read difficulty through the canonical conversion
      // boundary instead of branding the raw value inline. Inside this
      // finite-guarded branch getQuestionDifficulty(question) is identical to
      // toIRTDifficulty(rawDifficulty), but it makes difficulty.ts's
      // documented read-path a real consumer rather than a dead export.
      const branded = getQuestionDifficulty(question);
      // P2 CALIBRATION (mei 2026): pass module so per-module curves apply.
      // TAA reading-comprehension sits ~0.15 below the generic curve at
      // tier >= 9 — see MODULE_DIFFICULTY_CENTER_OFFSET in difficulty.ts.
      if (!isDifficultyConsistentWithTier(branded, question.tier, question.module, question.questionType, question.familyId)) {
        const band = expectedDifficultyBandForTier(question.tier, question.module, question.questionType, question.familyId);
        issues.push(coach('schema', STAGE, 'SCHEMA_DIFFICULTY_TIER_INVERSION', `citoDifficulty ${rawDifficulty} sits outside the expected band [${band.min.toFixed(2)}..${band.max.toFixed(2)}] for tier ${question.tier} — possible inversion or mis-tagging`, {
          field: 'citoDifficulty',
          expected: `${band.min.toFixed(2)}..${band.max.toFixed(2)}`,
          actual: String(rawDifficulty),
          questionId: qId,
          familyId: fId,
          severity: SEVERITY.MEDIUM,
          suggestion: 'Convention is IRT-style: higher value = harder question. A low value on a high tier (or vice versa) suggests the seed value is inverted.'
        }));
      }
    }
  }
  return issues;
}